import { TuiSelect, TuiInputDate, TuiChevron } from '@taiga-ui/kit';
import { formatDate } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TuiDay } from '@taiga-ui/cdk';
import {
  TuiDataList,
  TuiButton,
  TuiDropdown,
  TuiTextfield,
  TuiLabel,
  TuiCalendar,
} from '@taiga-ui/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, switchMap } from 'rxjs';
import type {
  LogisticDateTimeTransferReasonDto,
  UpdateLogisticRequestReleaseDateRequest,
} from '@outkeep/shared';
import { RequestsApiService } from '@core/services/request/requests-api.service';
import { RequestService } from '@core/services/request/request.service';

@Component({
  selector: 'x-change-release-date-group-actions-view',
  imports: [
    TuiTextfield,
    TuiLabel,
    TuiChevron,
    TuiCalendar,
    ReactiveFormsModule,
    TuiButton,
    TuiDataList,
    TuiInputDate,
    TuiSelect,
    TranslatePipe,
    TuiDropdown,
  ],
  templateUrl: './change-release-date-group-actions-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeReleaseDateGroupActionsViewComponent implements OnInit {
  readonly $viewObserver = input.required<Subject<never>>({
    alias: 'viewObserver',
  });

  private readonly destroyRef = inject(DestroyRef);
  private readonly requestsApiService = inject(RequestsApiService);
  private readonly requestService = inject(RequestService);

  readonly hours = Array.from({ length: 24 }, (_, hour) =>
    hour.toString().padStart(2, '0'),
  );
  readonly minutes = ['00', '15', '30', '45'];
  readonly $reasons = signal<LogisticDateTimeTransferReasonDto[]>([]);
  readonly stringifyReason = (reasonId: number | null): string => {
    const found = this.$reasons().find(reason => reason.id === reasonId);

    return found?.name ?? '';
  };
  readonly form = new FormGroup({
    releaseDate: new FormControl<TuiDay | null>(null),
    hour: new FormControl<string | null>(null),
    minute: new FormControl<string | null>(null),
    transferReason: new FormGroup({
      reasonId: new FormControl<number | null>(null),
    }),
  });

  get isTimeSelectionIncomplete(): boolean {
    const { hour, minute } = this.form.getRawValue();

    return (hour === null) !== (minute === null);
  }

  get hasAnyValue(): boolean {
    const { releaseDate, hour, minute } = this.form.getRawValue();

    return releaseDate !== null || hour !== null || minute !== null;
  }

  get isTransferReasonMissing(): boolean {
    const reasonId = this.form.controls.transferReason.controls.reasonId.value;

    return this.requiresTransferReason() && reasonId === null;
  }

  ngOnInit(): void {
    this.requestsApiService
      .getDateTimeTransferReasons$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ items }) => this.$reasons.set(items),
        error: () => this.$reasons.set([]),
      });
  }

  apply(): void {
    const payload = this.getPayload();

    if (!payload) {
      return;
    }

    this.requestsApiService
      .updateRequestsReleaseDate$(payload)
      .pipe(
        switchMap(() => this.requestService.reloadSelectedRequests$()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.$viewObserver().complete());
  }

  private getPayload(): UpdateLogisticRequestReleaseDateRequest | null {
    const { releaseDate, hour, minute, transferReason } =
      this.form.getRawValue();

    if (
      !this.hasAnyValue ||
      this.isTimeSelectionIncomplete ||
      this.isTransferReasonMissing
    ) {
      return null;
    }

    const requestIds = this.requestService
      .selectedRequests()
      .map(({ id }) => id);
    const payload: UpdateLogisticRequestReleaseDateRequest = { requestIds };

    if (releaseDate !== null) {
      payload.releaseDate = formatDate(
        releaseDate.toLocalNativeDate(),
        'yyyy-MM-dd',
        'en-CA',
      );
    }

    if (hour !== null && minute !== null) {
      payload.releaseTime = `${hour}:${minute}`;
    }

    if (transferReason.reasonId !== null) {
      payload.dateTimeTransferReasonId = transferReason.reasonId;
    }

    return payload;
  }

  private requiresTransferReason(): boolean {
    const { releaseDate, hour, minute } = this.form.getRawValue();
    const targetDate =
      releaseDate === null
        ? undefined
        : formatDate(releaseDate.toLocalNativeDate(), 'yyyy-MM-dd', 'en-CA');
    const targetTime =
      hour === null || minute === null ? undefined : `${hour}:${minute}`;

    return this.requestService.selectedRequests().some(request => {
      const currentDate = request.releaseDate?.slice(0, 10) ?? null;
      const currentTime = request.releaseTime ?? null;

      return (
        (targetDate !== undefined &&
          currentDate !== null &&
          currentDate !== targetDate) ||
        (targetTime !== undefined &&
          currentTime !== null &&
          currentTime !== targetTime)
      );
    });
  }
}
